import {
  Button,
  Dialog,
  Dropdown,
  NumberField,
  Tag,
  Typography,
} from "@hellocarbo/lasagne";
import { use, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSubmit } from "react-router";
import { useDebouncedCallback } from "use-debounce";

import { PortalContainerContext } from "@/contexts";
import { TTrajectoryAction } from "@/types";
import { formatCarbon } from "@/utils/carbon";

import ActionTags from "./ActionTags";

interface ActionDialogProps extends Omit<
  TTrajectoryAction,
  | "id"
  | "name_fr"
  | "name_en"
  | "content_fr"
  | "content_en"
  | "is_intensity_editable"
> {
  name: string;
  content: string;
  isReadOnly?: boolean;
  actionId: number;
  intensityIsEditable: boolean;
}

function ActionDialog({
  name,
  content,
  recommended,
  reduction_factor,
  category,
  difficulty,
  intensity_percentage,
  examples,
  reduction_tco2e,
  isReadOnly = true,
  actionId,
  intensityIsEditable,
}: ActionDialogProps) {
  const { container } = use(PortalContainerContext);
  const { t } = useTranslation(["formatter", "trajectory"]);
  const [dropdownContainer, setDropdownContainer] =
    useState<HTMLDivElement | null>(null);

  const submit = useSubmit();

  const { formattedCarbon: formattedReductionCarbon, unit: reductionUnit } =
    formatCarbon(t, reduction_tco2e);

  const validateIntensityPercentage = (value: number) => {
    if (value > 0 && value <= 100) {
      return true;
    }
    return false;
  };

  const debouncedOnIntensityChange = useDebouncedCallback(
    (newIntensityPercentage: number) => {
      const isValid = validateIntensityPercentage(newIntensityPercentage);
      if (isValid) {
        submit(
          {
            _intent: "updateTrajectoryAction",
            actionId,
            intensityPercentage: newIntensityPercentage,
          },
          { method: "PATCH" }
        );
      }
    },
    500
  );

  const showIntensityPercentageField =
    typeof intensity_percentage !== "undefined" &&
    intensity_percentage !== null &&
    intensityIsEditable;

  return (
    <Dialog
      portalContainer={container as Element}
      trigger={
        <button className="border-grayscale-xx-light px-lg py-md hover:border-grayscale-x-light focus:border-grayscale-x-light active:border-grayscale-x-light w-full rounded-lg border text-left shadow-sm hover:shadow-lg focus:shadow-lg active:shadow-lg">
          <Typography step="-1" weight="bold">
            {name}
          </Typography>
          <ActionTags
            reductionFactor={reduction_factor}
            recommended={recommended}
            category={category}
            difficulty={difficulty}
            className="mt-xs"
          />
        </button>
      }
      size="large"
      title={name}
    >
      <div ref={setDropdownContainer} />
      <ActionTags
        reductionFactor={reduction_factor}
        recommended={recommended}
        category={category}
        difficulty={difficulty}
        className="mb-xl"
      />
      <div className="my-xl">
        <div className="gap-lg flex flex-col xl:flex-row xl:items-end">
          {showIntensityPercentageField && (
            <div className="w-[280px]">
              <NumberField
                defaultValue={intensity_percentage}
                label={t("trajectory:actionPlan.actionAmbition")}
                unit="%"
                size="medium"
                fullWidth
                disabled={isReadOnly}
                onChange={debouncedOnIntensityChange}
                max={100}
                min={1}
              />
            </div>
          )}
          <Dropdown
            portalContainer={dropdownContainer as Element}
            label={t("trajectory:actionPlan.actionDifficulty")}
            className="w-[280px]"
            size="medium"
            defaultValue={difficulty}
            onChange={(newDifficulty) => {
              submit(
                {
                  _intent: "updateTrajectoryAction",
                  actionId,
                  difficulty: newDifficulty,
                },
                { method: "PATCH" }
              );
            }}
            groups={[
              {
                items: [
                  {
                    label: t("trajectory:actionPlan.difficulty.low"),
                    value: "low",
                  },
                  {
                    label: t("trajectory:actionPlan.difficulty.medium"),
                    value: "medium",
                  },
                  {
                    label: t("trajectory:actionPlan.difficulty.high"),
                    value: "high",
                  },
                ],
              },
            ]}
          />
        </div>
        <div className="gap-x-3xs mt-2xs flex items-center">
          <Typography step="-1" weight="bold" className="text-grayscale">
            {t("trajectory:actionPlan.cO2eReduction")}
          </Typography>
          <Tag
            intent="positive"
            text={t("formatter:percentage", {
              value: -reduction_factor,
              signDisplay: "exceptZero",
              maximumSignificantDigits: 2,
            })}
          />
          <Typography step="-1" className="text-grayscale">
            {t("trajectory:actionPlan.reductionByYear", {
              valueAndUnit: `${formattedReductionCarbon} ${reductionUnit}`,
            })}
          </Typography>
        </div>
      </div>
      <Typography step="1" weight="bold" as="h5" className="mb-sm">
        {t("trajectory:actionPlan.actionDescription")}
      </Typography>
      <Typography className="mb-md leading-lg" as="div">
        <div
          className="[&_a]:text-primary [&_a]:underline [&_a:hover]:opacity-80"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </Typography>
      {examples.length ? (
        <>
          <Typography weight="bold" className="mb-3xs">
            {t("trajectory:actionPlan.theyDidIt")}
          </Typography>
          <ul className="gap-x-sm flex items-center">
            {examples.map((example) => (
              <li key={example.label}>
                <Button
                  intent="secondary"
                  shape="link"
                  as="a"
                  href={example.url}
                  shouldOpenNewTab
                  size="small"
                >
                  {example.label}
                </Button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Dialog>
  );
}

export default ActionDialog;
